export interface CollageSlot {
  /** Normalized 0-100 rect within the canvas. */
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CollageLayout {
  id: string;
  name: string;
  /** Max photos this layout has slots for. */
  maxPhotos: number;
  /** CSS aspect-ratio string ("width/height") — not every layout is portrait. */
  aspect: string;
  slots: CollageSlot[];
}

/**
 * Grid layout catalog (catalog pattern copied from musicTracks.ts). Six
 * layouts; Filmstrip is a *layout*, not a theme — sprocket holes only read
 * correctly when the canvas itself is shaped like a reel with reserved
 * margins, so its slots are deliberately inset from the top/bottom edges to
 * leave room for renderCollageCanvas.ts to draw real sprocket rails there.
 * Any of the four collageThemes.ts entries can still be applied on top of it.
 */
export const COLLAGE_LAYOUTS: CollageLayout[] = [
  {
    id: 'classic2x2',
    name: '2 × 2',
    maxPhotos: 4,
    aspect: '4/5',
    slots: [
      { x: 0, y: 0, w: 50, h: 50 },
      { x: 50, y: 0, w: 50, h: 50 },
      { x: 0, y: 50, w: 50, h: 50 },
      { x: 50, y: 50, w: 50, h: 50 },
    ],
  },
  {
    id: 'triptych',
    name: 'Triptych',
    maxPhotos: 3,
    aspect: '4/5',
    slots: [
      { x: 0, y: 0, w: 33.33, h: 100 },
      { x: 33.33, y: 0, w: 33.34, h: 100 },
      { x: 66.67, y: 0, w: 33.33, h: 100 },
    ],
  },
  {
    id: 'featured',
    name: 'Featured',
    maxPhotos: 4,
    aspect: '4/5',
    slots: [
      { x: 0, y: 0, w: 62, h: 100 },
      { x: 62, y: 0, w: 38, h: 33.3 },
      { x: 62, y: 33.3, w: 38, h: 33.3 },
      { x: 62, y: 66.6, w: 38, h: 33.4 },
    ],
  },
  {
    id: 'mosaic',
    name: 'Mosaic',
    maxPhotos: 6,
    aspect: '4/5',
    slots: [
      { x: 0, y: 0, w: 60, h: 60 },
      { x: 60, y: 0, w: 40, h: 30 },
      { x: 60, y: 30, w: 40, h: 30 },
      { x: 0, y: 60, w: 33.3, h: 40 },
      { x: 33.3, y: 60, w: 33.4, h: 40 },
      { x: 66.7, y: 60, w: 33.3, h: 40 },
    ],
  },
  {
    id: 'spotlight',
    name: 'Spotlight',
    maxPhotos: 1,
    aspect: '4/5',
    slots: [{ x: 0, y: 0, w: 100, h: 100 }],
  },
  {
    id: 'diptych',
    name: 'Diptych',
    maxPhotos: 2,
    aspect: '4/5',
    slots: [
      { x: 0, y: 0, w: 50, h: 100 },
      { x: 50, y: 0, w: 50, h: 100 },
    ],
  },
  {
    id: 'filmstrip',
    name: 'Filmstrip',
    maxPhotos: 4,
    aspect: '2.5/1',
    slots: [
      { x: 1.5, y: 12, w: 22.5, h: 76 },
      { x: 25.5, y: 12, w: 22.5, h: 76 },
      { x: 49.5, y: 12, w: 22.5, h: 76 },
      { x: 73.5, y: 12, w: 25, h: 76 },
    ],
  },
];

export const DEFAULT_LAYOUT_ID = 'classic2x2';

export function resolveLayout(id: string | null | undefined): CollageLayout {
  return COLLAGE_LAYOUTS.find(l => l.id === id) ?? COLLAGE_LAYOUTS.find(l => l.id === DEFAULT_LAYOUT_ID)!;
}

/** Best-fitting layout for a given photo count — used by "Auto-arrange". Never picks Filmstrip (a deliberate style choice, not a default fit). */
export function suggestLayoutForCount(count: number): CollageLayout {
  const candidates = COLLAGE_LAYOUTS.filter(l => l.id !== 'filmstrip');
  const byMaxPhotosDesc = [...candidates].sort((a, b) => b.maxPhotos - a.maxPhotos);
  return (
    candidates.find(l => l.maxPhotos === count) ??
    byMaxPhotosDesc.find(l => l.maxPhotos <= count) ??
    candidates[0]
  );
}
